<template>
  <div class="payment-receipt-detail-page">
    <ExpenseReceiptsPageShell
      :title="headerTitle"
      :actions="headerActions"
      @action="onHeaderAction"
    >
      <template #left>
        <div class="payment-receipt-detail-header-left">
          <AppBreadcrumb
            :items="breadcrumbItems"
            :aria-label="breadcrumbAriaLabel"
            :show-back="true"
            :back-label="breadcrumbBackLabel"
            :fallback-target="{ name: 'expenseReceipts' }"
          />

          <div class="app-detail-header payment-receipt-detail-heading-wrap">
            <div class="app-detail-heading payment-receipt-detail-heading-content">
              <div class="payment-receipt-detail-heading-main">
                <h2 class="payment-receipt-detail-heading-title">{{ detailTitle }}</h2>
                <span
                  class="app-status-pill app-detail-status-pill"
                  :class="isCanceled ? 'payment-receipts-status-pill--canceled' : 'payment-receipts-status-pill--active'"
                >
                  {{ statusLabel }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #actions="{ onAction }">
        <div class="app-page-header-actions app-detail-header-actions">
          <button
            v-for="action in primaryHeaderActions"
            :key="action.key"
            class="app-header-btn"
            :class="[
              `app-header-btn--${action.variant || 'primary'}`,
              {
                'payment-receipt-detail-header-btn--icon-only': action.iconOnly,
                'payment-receipt-detail-header-btn--download-bundle': action.key === 'download',
              }
            ]"
            type="button"
            :title="String(action?.title || action?.label || '')"
            @click="handlePrimaryHeaderAction(action, onAction)"
          >
            <i v-if="action.icon" class="fa" :class="action.icon" aria-hidden="true"></i>
            <span v-if="!action.iconOnly">{{ action.label }}</span>
          </button>

          <div v-if="secondaryHeaderActions.length" class="app-detail-more-actions-wrap" ref="moreActionsWrap">
            <button
              type="button"
              class="app-header-btn app-header-btn--ghost app-detail-more-actions-btn"
              :title="text.detail.header.moreActionsTitle"
              @click.stop="toggleMoreActionsMenu"
            >
              <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
            </button>

            <div v-if="showMoreActionsMenu" class="app-detail-more-actions-menu">
              <button
                v-for="action in secondaryHeaderActions"
                :key="action.key"
                type="button"
                :class="{ 'payment-receipt-detail-more-actions-menu__item--critical': action.key === 'cancel' }"
                @click="handleSecondaryHeaderAction(action, onAction)"
              >
                <span>{{ action.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </template>

      <template #content>
        <section class="payment-receipt-detail-content-wrap">
          <AppSectionTabs
            v-model="activeTab"
            :tabs="detailTabs"
            :aria-label="sectionTabsAriaLabel"
          />

          <ExpenseReceiptDetailTabsPanel
            :active-tab="activeTab"
            :expense-receipt="expenseReceiptData"
          />
        </section>
      </template>
    </ExpenseReceiptsPageShell>

    <SendEmailModal
      context-type="expense_receipt"
      :context-data="sendEmailContextData"
      :show="showSendEmailModal"
      :submitting="sendEmailModalSubmitting"
      @close="closeSendEmailModal"
      @submit="submitSendEmailModal"
    />

    <ConfirmActionModal
      :show="showCancelConfirmModal"
      tone="danger"
      :loading="cancelExpenseReceiptSubmitting"
      :title="cancelConfirmTitle"
      :subtitle="cancelConfirmSubtitle"
      :message="cancelConfirmMessage"
      :confirm-text="cancelConfirmButton"
      :cancel-text="cancelConfirmDismiss"
      @cancel="closeCancelConfirmModal"
      @confirm="confirmCancelExpenseReceipt"
    />
  </div>
</template>

<script>
import AppBreadcrumb from '@/components/common/AppBreadcrumb.vue';
import AppSectionTabs from '@/components/common/AppSectionTabs.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import SendEmailModal from '@/components/sendEmail/SendEmailModal.vue';
import ExpenseReceiptsPageShell from '@/components/expenseReceipts/ExpenseReceiptsPageShell.vue';
import ExpenseReceiptDetailTabsPanel from '@/components/expenseReceipts/detail/ExpenseReceiptDetailTabsPanel.vue';
import expenseReceiptsService from '@/services/expenseReceipts';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractPayload,
} from '@/utils/dataNormalization';
import { downloadBlobFile } from '@/utils/fileDownload';
import { useDomainText } from '@/composables/text/useDomainText';
import { buildInvoiceDetailHeaderActions } from '@/composables/invoices/detail/helpers';
import { invoiceDetailSendEmailMethods } from '@/composables/send/sendEmailMethods';

const { catalog: expenseReceiptsText } = useDomainText('expenseReceipts');
const { catalog: sendEmailText } = useDomainText('sendEmail');

export default {
  name: 'ExpenseReceiptDetailPage',
  components: {
    AppBreadcrumb,
    AppSectionTabs,
    ConfirmActionModal,
    SendEmailModal,
    ExpenseReceiptsPageShell,
    ExpenseReceiptDetailTabsPanel,
  },
  data() {
    return {
      text: expenseReceiptsText,
      sendEmailText,
      activeTab: 'detail',
      expenseReceiptData: null,
      showMoreActionsMenu: false,
      showSendEmailModal: false,
      sendEmailModalSubmitting: false,
      showCancelConfirmModal: false,
      cancelExpenseReceiptSubmitting: false,
    };
  },
  computed: {
    expenseReceiptId() {
      return Number(this.$route?.params?.expenseReceiptId || 0) || 0;
    },
    headerTitle() {
      return this.detailTitle;
    }, 
    detailTitle() {
      const pageTitle = String(this.text?.detail?.page?.title || 'Pago').trim();
      const folio = String(this.expenseReceiptData?.folio || '').trim();
      return folio !== '' ? `${pageTitle} ${folio}`.trim() : pageTitle;
    },
    statusLabel() {
      return String(this.expenseReceiptData?.status || '').trim() || String(this.text?.detail?.status?.active || 'VIGENTE');
    },
    isCanceled() {
      return String(this.expenseReceiptData?.status_key || '').trim().toLowerCase() === 'canceled';
    },
    isStampedExpenseReceipt() {
      return Number(this.expenseReceiptData?.is_stamped || 0) === 1;
    },
    headerActions() {
      const labels = this.text?.detail?.headerActions || {};
      const actions = buildInvoiceDetailHeaderActions(labels);
      return actions.filter((action) => {
        const key = String(action?.key || '');
        if (key === 'clone' || key === 'pay') {
          return false;
        }

        if (!this.isStampedExpenseReceipt && (key === 'download' || key === 'downloadXml')) {
          return false;
        }

        return true;
      });
    },
    primaryHeaderActions() {
      const actions = Array.isArray(this.headerActions) ? this.headerActions : [];
      const primaryKeys = this.isStampedExpenseReceipt
        ? ['downloadXml', 'send']
        : ['downloadPdf', 'send'];

      return actions.filter((action) => primaryKeys.includes(String(action?.key || '')));
    },
    secondaryHeaderActions() {
      const actions = Array.isArray(this.headerActions) ? this.headerActions : [];
      const primaryKeys = new Set(this.primaryHeaderActions.map((action) => String(action?.key || '')));
      const secondary = actions.filter((action) => !primaryKeys.has(String(action?.key || '')));
      const priorityByKey = {
        downloadPdf: 1,
        download: 2,
      };

      return secondary.slice().sort((left, right) => {
        const leftKey = String(left?.key || '');
        const rightKey = String(right?.key || '');
        const leftPriority = priorityByKey[leftKey] || 99;
        const rightPriority = priorityByKey[rightKey] || 99;

        if (leftPriority !== rightPriority) {
          return leftPriority - rightPriority;
        }

        return 0;
      });
    },
    cancelConfirmTitle() {
      return String(this.text?.detail?.cancelConfirm?.title || 'Cancelar pago de egreso').trim() || 'Cancelar pago de egreso';
    },
    cancelConfirmSubtitle() {
      return String(this.text?.detail?.cancelConfirm?.subtitle || 'Confirma para continuar con esta operación.').trim()
        || 'Confirma para continuar con esta operación.';
    },
    cancelConfirmMessage() {
      return String(this.text?.detail?.cancelConfirm?.message || 'El pago de egreso se cancelará y los gastos relacionados volverán a pendiente. Esta acción no se puede deshacer.').trim()
        || 'El pago de egreso se cancelará y los gastos relacionados volverán a pendiente. Esta acción no se puede deshacer.';
    },
    cancelConfirmButton() {
      return String(this.text?.detail?.cancelConfirm?.confirm || 'Sí, cancelar pago').trim() || 'Sí, cancelar pago';
    },
    cancelConfirmDismiss() {
      return String(this.text?.detail?.cancelConfirm?.cancel || 'Volver').trim() || 'Volver';
    },
    detailTabs() {
      const tabLabels = this.text?.detail?.tabs || {};
      return [
        { key: 'detail', label: String(tabLabels.detail || '').trim() },
        { key: 'shipping', label: String(tabLabels.shipping || '').trim() },
        { key: 'documents', label: String(tabLabels.documents || '').trim() },
        { key: 'history', label: String(tabLabels.history || '').trim() },
      ];
    },
    sectionTabsAriaLabel() {
      return String(this.text?.detail?.sectionTabsAriaLabel || '').trim();
    },
    breadcrumbItems() {
      return [
        { label: String(this.text?.page?.title || 'Pagos').trim(), to: { name: 'expenseReceipts' } },
        { label: this.detailTitle },
      ];
    },
    breadcrumbAriaLabel() {
      return String(this.text?.detail?.breadcrumb?.ariaLabel || 'Ruta de pago').trim();
    },
    breadcrumbBackLabel() {
      return String(this.text?.detail?.breadcrumb?.backLabel || 'Volver').trim();
    },
    sendEmailContextData() {
      const expenseReceiptData = this.expenseReceiptData && typeof this.expenseReceiptData === 'object'
        ? this.expenseReceiptData
        : {};

      return {
        id: this.expenseReceiptId,
        total: expenseReceiptData?.total,
        supplierId: expenseReceiptData?.supplier_id || expenseReceiptData?.provider_id,
        providerId: expenseReceiptData?.provider_id || expenseReceiptData?.supplier_id,
        supplierName: String(expenseReceiptData?.supplier_name || expenseReceiptData?.receiver_business_name || '').trim(),
        supplierEmail: String(expenseReceiptData?.supplier_email || expenseReceiptData?.provider_email || expenseReceiptData?.email || '').trim(),
        currency: String(expenseReceiptData?.currency || '').trim(),
        expenseReceiptData,
      };
    },
  },
  created() {
    this.loadExpenseReceiptDetail();
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    handlePrimaryHeaderAction(action, onAction) {
      this.showMoreActionsMenu = false;
      onAction?.(action);
    },
    handleSecondaryHeaderAction(action, onAction) {
      this.showMoreActionsMenu = false;
      onAction?.(action);
    },
    toggleMoreActionsMenu() {
      this.showMoreActionsMenu = !this.showMoreActionsMenu;
    },
    handleDocumentClick(event) {
      const target = event?.target;
      if (target && target.closest('.app-detail-more-actions-wrap')) {
        return;
      }

      this.showMoreActionsMenu = false;
    },
    onHeaderAction(action) {
      const actionKey = String(action?.key || '').trim();

      if (actionKey === 'send') {
        this.openSendEmailModal();
        return;
      }

      if (actionKey === 'downloadPdf') {
        this.downloadExpenseReceiptPdf();
        return;
      }

      if (actionKey === 'downloadXml') {
        this.downloadExpenseReceiptXml();
        return;
      }

      if (actionKey === 'download') {
        this.downloadExpenseReceiptZip();
        return;
      }

      if (actionKey === 'cancel') {
        this.openCancelConfirmModal();
        return;
      }

      const actionToasts = this.text?.detail?.toast?.actions || {};
      const fallbackActionKey = actionKey === 'download' ? 'downloadPdf' : actionKey;
      const message = String(actionToasts?.[actionKey] || actionToasts?.[fallbackActionKey] || '').trim();
      if (message !== '') {
        this.$toast?.info(message);
      }
    },
    openCancelConfirmModal() {
      if (this.expenseReceiptId <= 0 || this.isCanceled) {
        return;
      }

      this.showCancelConfirmModal = true;
    },
    closeCancelConfirmModal() {
      if (this.cancelExpenseReceiptSubmitting) {
        return;
      }

      this.showCancelConfirmModal = false;
    },
    async confirmCancelExpenseReceipt() {
      if (this.cancelExpenseReceiptSubmitting || this.expenseReceiptId <= 0 || this.isCanceled) {
        return;
      }

      this.cancelExpenseReceiptSubmitting = true;
      try {
        const response = await expenseReceiptsService.cancel(this.expenseReceiptId, {});
        const apiErrorMessage = extractApiErrorMessage(response, 'No fue posible cancelar el pago de egreso.');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, 'Pago de egreso cancelado correctamente.'));
        this.showCancelConfirmModal = false;
        await this.loadExpenseReceiptDetail();
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || 'No fue posible cancelar el pago de egreso.');
      } finally {
        this.cancelExpenseReceiptSubmitting = false;
      }
    },
    buildExpenseReceiptDocumentBaseName() {
      const folio = String(this.expenseReceiptData?.folio || '').trim();
      const id = Number(this.expenseReceiptData?.id || this.expenseReceiptId || 0) || 0;
      const fallback = id > 0 ? `pago_${id}` : 'pago';

      return (folio || fallback)
        .replace(/\s+/g, '_')
        .replace(/[^A-Za-z0-9_\-]/g, '') || fallback;
    },
    async downloadExpenseReceiptPdf() {
      if (this.expenseReceiptId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      try {
        const response = await expenseReceiptsService.downloadPdf(this.expenseReceiptId);
        this.openPdfBlob(response?.data ?? response);
      } catch (error) {
        const fallback = String(this.text?.detail?.toast?.actions?.downloadPdf || 'No fue posible descargar el PDF del pago.');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    async downloadExpenseReceiptXml() {
      if (this.expenseReceiptId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      if (!this.isStampedExpenseReceipt) {
        return;
      }

      try {
        const response = await expenseReceiptsService.downloadXml(this.expenseReceiptId);
        downloadBlobFile(response?.data ?? response, `${this.buildExpenseReceiptDocumentBaseName()}.xml`, 'application/xml');
      } catch (error) {
        const fallback = String(this.text?.detail?.toast?.actions?.downloadXml || 'No fue posible descargar el XML del pago.');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    async downloadExpenseReceiptZip() {
      if (this.expenseReceiptId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        return;
      }

      if (!this.isStampedExpenseReceipt) {
        return;
      }

      try {
        const response = await expenseReceiptsService.downloadZip(this.expenseReceiptId);
        downloadBlobFile(response?.data ?? response, `${this.buildExpenseReceiptDocumentBaseName()}_cfdi.zip`, 'application/zip');
      } catch (error) {
        const fallback = String(this.text?.detail?.toast?.actions?.download || 'No fue posible descargar el ZIP del pago.');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        this.$toast?.error(message || fallback);
      }
    },
    openPdfBlob(payload) {
      const blob = payload instanceof Blob
        ? payload
        : new Blob([payload], { type: 'application/pdf' });

      const objectUrl = window.URL.createObjectURL(blob);
      const opened = window.open(objectUrl, '_blank', 'noopener');

      if (!opened) {
        downloadBlobFile(blob, `${this.buildExpenseReceiptDocumentBaseName()}.pdf`, 'application/pdf');
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60 * 1000);
    },
    async loadExpenseReceiptDetail() {
      if (this.expenseReceiptId <= 0) {
        this.$toast?.error(String(this.text?.toasts?.openDetailError || ''));
        this.$router.replace({ name: 'expenseReceipts' });
        return;
      }

      try {
        const response = await expenseReceiptsService.show(this.expenseReceiptId);
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage !== '') {
          this.$toast?.error(apiErrorMessage);
          this.$router.replace({ name: 'expenseReceipts' });
          return;
        }

        const payload = extractPayload(response);
        if (payload && typeof payload === 'object' && Number(payload?.id || 0) > 0) {
          this.expenseReceiptData = payload;
          return;
        }
      } catch (error) {
        const fallback = String(this.text?.detail?.toast?.loadExpenseReceiptError || 'No fue posible cargar el detalle del pago.');
        const message = extractApiErrorMessage(error?.response || error, fallback);
        if (String(message || '').trim() !== '') {
          this.$toast?.error(message);
        }
      }

      this.$router.replace({ name: 'expenseReceipts' });
    },
    ...invoiceDetailSendEmailMethods,
  },
};
</script>

<style scoped>
.payment-receipt-detail-header-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.payment-receipt-detail-content-wrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-receipt-detail-heading-wrap {
  align-items: center;
}

.payment-receipt-detail-heading-content {
  width: 100%;
}

.payment-receipt-detail-heading-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.payment-receipt-detail-heading-title {
  margin: 0;
}

.payment-receipt-detail-more-actions-menu__item--critical {
  color: #b91c1c;
}

.payment-receipt-detail-more-actions-menu__item--critical:hover {
  background: rgba(185, 28, 28, 0.08);
  color: #991b1b;
}

.payment-receipt-detail-header-btn--icon-only {
  min-width: 36px;
  padding-left: 10px;
  padding-right: 10px;
}

.payment-receipt-detail-header-btn--download-bundle {
  background: rgba(31, 42, 61, 0.1);
  color: #1f2a3d;
  border: 1px solid rgba(31, 42, 61, 0.2);
}

.payment-receipt-detail-header-btn--download-bundle:not(:disabled):hover {
  background: rgba(31, 42, 61, 0.16);
}

.payment-receipts-status-pill--active {
  color: #1c9b43;
  background: rgba(59, 212, 46, 0.14);
  border: 1px solid rgba(59, 212, 46, 0.3);
}

.payment-receipts-status-pill--canceled {
  color: #b42318;
  background: #fff0f0;
  border: 1px solid rgba(197, 58, 58, 0.3);
}
</style>
